import { accountAccess } from '@app/+authenticated/+account/account.access';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { canViewEmployeeProfile } from '@app/reducers/orm/employee/employee-profile-access.helper';
import {
  getEmployeeEntities,
  getEmployeeTeamDepartments,
  getEmployeeTeamDepartmentsWithoutFlexpool,
} from '@app/reducers/orm/employee/employee.selectors';
import { createSelector } from '@ngrx/store';
import { hasPermission } from '@sb/shared/permission/util';

/**
 * Checks if the user can manage account for any department.
 * This usually means they will be able to manage subscription and billing and also settings.
 * @returns True if the user can manage account for any department, false otherwise.
 */
export const selectPermissionCanManageAccount = createSelector(getPermissionState, (permissionState) =>
  hasPermission({ permissions: 'Manage account', userId: 'me', departments: 'any' }, permissionState),
);

/**
 * Checks if the user can open the account settings area at all — the same permission set that guards
 * the `/account` route. Wider than "Manage account": App center managers are in it too.
 */
export const selectPermissionCanAccessAccountSettings = createSelector(getPermissionState, (permissionState) =>
  hasPermission({ permissions: accountAccess.root.permission, userId: 'me', departments: 'any' }, permissionState),
);

/**
 * Answers per employee whether `EmployeeDetailGuard` lets the user open their profile pages.
 */
export const selectCanViewEmployeeProfile = createSelector(
  getPermissionState,
  getEmployeeTeamDepartmentsWithoutFlexpool,
  getEmployeeEntities,
  (permissionState, departments, employees) => (employeeId: string) =>
    !!employees[employeeId] &&
    !employees[employeeId].anonymized &&
    canViewEmployeeProfile(employeeId, departments[employeeId], permissionState),
);

/**
 * Answers per employee whether the user can open their contracts page, which
 * `EmployeePermissionGuard` also gates on View contracts in the employee's departments.
 */
export const selectCanViewEmployeeContracts = createSelector(
  getPermissionState,
  getEmployeeTeamDepartments,
  selectCanViewEmployeeProfile,
  (permissionState, departments, canViewProfile) => (employeeId: string) =>
    canViewProfile(employeeId) &&
    hasPermission(
      {
        permissions: 'View contracts',
        userId: employeeId,
        departments: departments[employeeId].filter((departmentId) => departmentId !== null),
      },
      permissionState,
    ),
);
